import { useState } from 'react'
import { ExaminationRoomDialog } from './ExaminationRoomDialog'
import { DeleteAlert } from './DeleteAlert'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { examinationRoomService } from '@/services/examination_service'
import { ColumnDef, RowData } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Settings } from 'lucide-react'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'

const CLINIC_DELETE_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.D },
  { module: 'provider_clinic_management', action: AllowedAction.D },
  { module: 'clinic_group_clinic_management', action: AllowedAction.D },
  { module: 'clinic_clinic_management', action: AllowedAction.D },
]

export type ExaminationRoom = {
  examination_room_id: number
  clinic_id: number
  name: string
  department: string | null
  group_id: number
  doctor_group_id: number
  is_active: boolean
  created_at: Date
  patient_flow_device_ip?: string
  clinic: {
    name: string
  }
}

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    reloadData?: () => void
    mutate?: () => void
  }
}

export const columns: ColumnDef<ExaminationRoom>[] = [
  {
    accessorKey: 'name',
    header: '診間名稱',
  },
  {
    accessorKey: 'department',
    header: '部門',
  },
  // {
  //   accessorKey: 'is_active',
  //   header: '狀態',
  //   cell: ({ row }) => <span>{row.original.is_active == true ? '啟用' : '停用'}</span>,
  // },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const examinationRoom = row.original
      const { token } = useAuth()
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)

      const handleDelete = async () => {
        if (!token) return

        try {
          const result = await examinationRoomService.deleteExaminationRoom(
            examinationRoom.examination_room_id,
            token,
          )

          if (result.success) {
            toast.success('刪除診間成功')
            if (table.options.meta?.mutate) {
              table.options.meta.mutate()
            }
          } else {
            toast.error('刪除診間失敗')
          }
        } catch {
          toast.error('刪除診間失敗')
        }
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setShowEditDialog(true)}>
                編輯基本資料
              </DropdownMenuItem>
              <IfAnyPermission permissions={CLINIC_DELETE_PERMISSIONS}>
                <DropdownMenuItem
                  className="text-destructive"
                  onSelect={() => setShowDeleteAlert(true)}
                >
                  刪除
                </DropdownMenuItem>
              </IfAnyPermission>
            </DropdownMenuContent>
          </DropdownMenu>

          <ExaminationRoomDialog
            examinationRoomId={examinationRoom.examination_room_id}
            clinicId={examinationRoom.clinic_id}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onMutate={table.options.meta?.mutate}
          />
          <IfAnyPermission permissions={CLINIC_DELETE_PERMISSIONS}>
            <DeleteAlert
              open={showDeleteAlert}
              onOpenChange={setShowDeleteAlert}
              onConfirm={handleDelete}
            />
          </IfAnyPermission>
        </>
      )
    },
  },
]
